import React, { useState } from "react";
import {
  CalendarDays,
  User,
  Phone,
  Mail,
  MessageSquare,
  CheckCircle,
  Car,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Car Wash - $3 / 3 min",
  "Vacuum - $3 / 3 min",
  "General Inquiry",
];

const PERKS = [
  { icon: CalendarDays, text: "Walk-ins always welcome — no booking needed" },
  { icon: Phone, text: "Call us with any questions anytime" },
  { icon: CheckCircle, text: "Coin & card accepted at all bays" },
  { icon: Car, text: "All vehicle sizes welcome" },
];

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.date) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/bookings`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", service: "", date: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-2xl border border-[#a8d8ea]/50 bg-[#f8fcff] text-[#1a2744] placeholder:text-[#1a2744]/35 focus:outline-none focus:border-[#a8d8ea] focus:ring-2 focus:ring-[#a8d8ea]/25 transition-all duration-200";

  const iconInputClass =
    "w-full pl-10 pr-4 py-3 rounded-2xl border border-[#a8d8ea]/50 bg-[#f8fcff] text-[#1a2744] placeholder:text-[#1a2744]/35 focus:outline-none focus:border-[#a8d8ea] focus:ring-2 focus:ring-[#a8d8ea]/25 transition-all duration-200";

  return (
    <section id="booking" className="py-24 bg-[#f0f9ff]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Info */}
          <div>
            <div className="inline-flex items-center gap-2 bg-[#a8d8ea]/40 text-[#1a2744] rounded-full px-4 py-2 text-sm font-bold mb-6 border border-[#a8d8ea]/30">
              <CalendarDays size={15} strokeWidth={2.5} className="text-[#1a2744]" />
              Reserve a Bay
            </div>
            <h2
              className="text-4xl sm:text-5xl font-bold text-[#1a2744] mb-6"
              style={{ fontFamily: "Fredoka, sans-serif" }}
              data-testid="booking-heading"
            >
              Get in Touch
            </h2>
            <p className="text-[#1a2744]/70 text-base sm:text-lg leading-relaxed mb-10">
              Have a question or want to reserve a bay ahead of time? Drop us a
              message and we'll get back to you quickly. Walk-ins are always
              welcome too — just pull up and get washing.
            </p>

            <div className="space-y-4">
              {PERKS.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-white border border-[#a8d8ea]/40 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Icon size={19} strokeWidth={2.5} className="text-[#1a2744]" />
                  </div>
                  <p className="text-[#1a2744]/70 font-semibold text-sm">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_8px_40px_rgba(26,39,68,0.10)] border border-[#a8d8ea]/30">
            <h3
              className="text-2xl font-bold text-[#1a2744] mb-6"
              style={{ fontFamily: "Fredoka, sans-serif" }}
            >
              Send Us a Message
            </h3>
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="booking-form"
            >
              {/* Name */}
              <div>
                <label className="block text-sm font-bold text-[#1a2744] mb-2">
                  Full Name <span className="text-[#c0392b]">*</span>
                </label>
                <div className="relative">
                  <User
                    size={15}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1a2744]/35"
                  />
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className={iconInputClass}
                    data-testid="booking-name-input"
                  />
                </div>
              </div>

              {/* Email + Phone row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#1a2744] mb-2">
                    Email <span className="text-[#c0392b]">*</span>
                  </label>
                  <div className="relative">
                    <Mail
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1a2744]/35"
                    />
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className={iconInputClass}
                      data-testid="booking-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#1a2744] mb-2">
                    Phone <span className="text-[#c0392b]">*</span>
                  </label>
                  <div className="relative">
                    <Phone
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1a2744]/35"
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className={iconInputClass}
                      data-testid="booking-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Service */}
              <div>
                <label className="block text-sm font-bold text-[#1a2744] mb-2">
                  Service <span className="text-[#c0392b]">*</span>
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  className={inputClass + " cursor-pointer"}
                  data-testid="booking-service-select"
                >
                  <option value="">Select a service...</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-bold text-[#1a2744] mb-2">
                  Preferred Date <span className="text-[#c0392b]">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                  className={inputClass}
                  data-testid="booking-date-input"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-bold text-[#1a2744] mb-2">
                  Additional Notes
                </label>
                <div className="relative">
                  <MessageSquare
                    size={15}
                    className="absolute left-3.5 top-3.5 text-[#1a2744]/35"
                  />
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Any special requests or notes about your vehicle..."
                    rows={3}
                    className={iconInputClass + " resize-none"}
                    data-testid="booking-message-textarea"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#a8d8ea] text-[#1a2744] rounded-full py-4 font-bold text-base hover:-translate-y-0.5 hover:bg-[#7fc8e0] hover:shadow-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                data-testid="booking-submit-button"
              >
                {loading ? "Submitting..." : "Send Message"}
              </button>

              <p className="text-center text-xs text-[#1a2744]/40 mt-2">
                Walk-ins are always welcome. We'll respond to messages within 24 hours.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
